import {Context, Channel, ChannelId, defaultChannel} from 'openfin-fdc3';
import {Identity} from 'openfin/_v2/identity';
import {createSelector} from 'reselect';

import {Action, KeyedAction} from '../Store';
import {RootState} from '../State';

export interface DisplayInfo {
    name: string;
    application: string;
    icon?: string;
}

export interface MemberInfo {
    identity: Identity;
    display: DisplayInfo;
    channel: Channel | null;
    visible: boolean;
}

export interface ChannelInfo extends ChannelData {
    members: MemberInfo[];
}

export interface ChannelData<C = Channel> {
    channel: C;
    context: Context | null;
}

export type IdentityKey = string;

export interface ChannelManagerState {
    channels: Record<ChannelId, ChannelData>;
    entities: Record<IdentityKey, MemberInfo>;
}

export const ChannelManagerInitialState: ChannelManagerState = {
    channels: {},
    entities: {}
};

export class JoinChannel extends KeyedAction<RootState, 'channelManager'> {
    public readonly type: string = '@@CHANNEL_MANAGER/JOIN_CHANNEL';

    constructor(public readonly channel: Channel, public readonly identity: Identity) {
        super('channelManager');
    }
}

export class SetChannel extends KeyedAction<RootState, 'channelManager'> {
    public readonly type: string = '@@CHANNEL_MANAGER/SET_CHANNEL';

    constructor(private readonly channelData: ChannelData) {
        super('channelManager');
    }

    public reduce(state: ChannelManagerState): ChannelManagerState {
        const {channelData} = this;
        const channels = {...state.channels};

        channels[channelData.channel.id] = channelData;

        return {
            ...state,
            channels
        };
    }
}

export class SetChannelContext extends KeyedAction<RootState, 'channelManager'> {
    public readonly type: string = '@@CHANNEL_MANAGER/SET_CHANNEL_CONTEXT';

    constructor(private readonly channelId: string, private readonly context: Context) {
        super('channelManager');
    }

    public reduce(state: ChannelManagerState): ChannelManagerState {
        const {channelId, context} = this;
        const channels = {...state.channels};

        channels[channelId].context = context;

        return {
            ...state,
            channels
        };
    }
}

/**
 * Update or partial diff change a entity's data.
 */
export class UpdateEntity extends KeyedAction<RootState, 'channelManager'> {
    public readonly type: string = '@@CHANNEL_MANAGER/UPDATE_ENTITY';

    constructor(private readonly identity: Identity, private readonly info: Partial<Omit<MemberInfo, 'identity'>>) {
        super('channelManager');
    }

    public reduce(state: ChannelManagerState): ChannelManagerState {
        const {info} = this;
        const identity = stripIdentity(this.identity);
        const identityKey = getIdentityKey(identity);

        // Write identity so it cannot be changed
        const newInfo: MemberInfo = {...state.entities[identityKey], ...info, identity};

        return {
            ...state,
            entities: {
                ...state.entities,
                [identityKey]: newInfo
            }
        };
    }
}

export class ToggleChannelManagerWindow extends Action<RootState> {
    public readonly type: string = '@@CHANNEL_MANAGER/LAUNCH_CHANNEL_MANAGER_WINDOW';
}

export function getIdentityKey(identity: Identity): IdentityKey {
    return `${identity.name}/${identity.uuid}`;
}

export function stripIdentity(identity: Identity): Identity {
    return {name: identity.name, uuid: identity.uuid};
}

export const getChannelMembersSelector = createSelector(getMemberInfos, buildChannelMembers);

export const getChannelInfo = (state: RootState, channelId: ChannelId): ChannelInfo => {
    const channel = state.channelManager.channels[channelId];
    const members = getChannelMembersSelector(state)[channelId];

    return {
        ...channel,
        members
    };
};

export const getChannelsInfo = (state: RootState): ChannelInfo[] => {
    // Place default channel last
    const channels = Object.keys(state.channelManager.channels).filter((id) => id !== defaultChannel.id).map((id) => getChannelInfo(state, id));
    channels.push(getChannelInfo(state, defaultChannel.id));
    return channels;
};

export function getMemberInfos(state: RootState): MemberInfo[] {
    return Object.values(state.channelManager.entities).filter((win) => !!win.channel);
}

function buildChannelMembers(windows: MemberInfo[]): Record<ChannelId, MemberInfo[]> {
    const channels: Record<ChannelId, MemberInfo[]> = {};

    for (const win of windows) {
        if (win.channel) {
            const channelId = win.channel.id;
            if (!channels[channelId]) {
                channels[channelId] = [];
            }
            channels[channelId].push(win);
        }
    }

    return channels;
}

